import { useAtom, useAtomValue } from 'jotai';
import { useEffect, useRef } from 'react';

import {
  CustomEventTypes,
  customEventAtom
} from '@/pages/Administration/Devices/Devices.store';
import { selectedOrganizationAtom } from '@/pages/Organizations';

/**
 * Custom hook for managing device auto-refresh timer
 *
 * Sets up a 5-minute interval to automatically refresh device data.
 * Clears the timer on unmount or when the selected organization changes.
 *
 * Also triggers refresh when custom events are dispatched (e.g., after device updates).
 *
 * @param devicesQuery - Tanstack query object for devices
 * @param isSyncInProgressQuery - Tanstack query object for sync status
 * @param closeDeviceDetail - Callback to close device detail panel
 *
 * @example
 * ```tsx
 * useDeviceAutoRefresh(devicesQuery, isSyncInProgressQuery, closeDeviceDetail);
 * ```
 */
export const useDeviceAutoRefresh = (
  devicesQuery: {
    isFetching: boolean;
    refetch: () => void;
  },
  isSyncInProgressQuery: {
    refetch: () => void;
  },
  closeDeviceDetail: () => void
) => {
  const [selectedOrgData] = useAtom(selectedOrganizationAtom);
  const [, setCustomEvent] = useAtom(customEventAtom);
  const customEventValue = useAtomValue(customEventAtom);
  const timerRef = useRef<ReturnType<typeof setInterval> | undefined>(
    undefined
  );

  /**
   * Set up auto-refresh timer
   * Refreshes every 5 minutes if data is not currently being fetched
   */
  useEffect(() => {
    closeDeviceDetail();

    // Clear existing timer
    if (timerRef.current) {
      clearInterval(timerRef.current);
    }

    // Set up new timer
    timerRef.current = setInterval(() => {
      if (selectedOrgData.id >= 0) {
        // Trigger device detail refresh if detail pane is open
        setCustomEvent(CustomEventTypes.REFRESH_DEVICE_DETAIL);

        // Refetch device data if not already fetching
        if (!devicesQuery.isFetching) {
          devicesQuery.refetch();
          isSyncInProgressQuery.refetch();
        }
      }
    }, 300000); // 5 minutes

    // Cleanup on unmount or org change
    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [selectedOrgData?.id]);

  /**
   * Handle custom events for manual refresh triggers
   */
  useEffect(() => {
    switch (customEventValue) {
      case CustomEventTypes.REFRESH_DEVICE_LISTING:
        setCustomEvent(CustomEventTypes.NONE);
        if (!devicesQuery.isFetching) {
          devicesQuery.refetch();
          isSyncInProgressQuery.refetch();
        }
        setCustomEvent(CustomEventTypes.REFRESH_DEVICE_DETAIL);
        break;
      case CustomEventTypes.CLOSE_DEVICE_DETAIL:
        setCustomEvent(CustomEventTypes.NONE);
        closeDeviceDetail();
        break;
      default:
        break;
    }
  }, [
    closeDeviceDetail,
    customEventValue,
    devicesQuery,
    isSyncInProgressQuery,
    setCustomEvent
  ]);
};
